/*
 * Cart lines in the drawer and on the cart page, after filamentor.cz:
 *
 * - Nothing ever locks the list. The theme (assets/component-cart-items.js)
 *   disables every row until a change is answered, because it addresses
 *   lines by position ("line 2"), which shifts after each change. Here every
 *   change is addressed by the line itself (variant + warranty link) and
 *   resolved against a fresh cart read right before it is sent, through the
 *   cart queue (assets/cart-queue.js).
 * - Removing a line collapses it at once (height and opacity, Web
 *   Animations). Quantity changes show at once; changes made in quick
 *   succession go out together. The header bubble follows immediately.
 * - A line that comes in grows into place. Rows have stable ids
 *   (snippets/cart-products.liquid), so a re-render (morph) only inserts or
 *   drops rows for lines that really came or went.
 * - Re-renders from responses that predate a newer change do not undo it:
 *   rows being removed stay collapsed and pending quantities stay shown.
 * - While a change is on its way, the prices it affects shimmer (the theme's
 *   text-component shimmer): the changed line's price and the cart total.
 *   The re-render with the confirmed prices ends it.
 * - + stops at the stock (data-stock-max from Liquid, or less when an add
 *   comes back capped) and says so on hover or click
 *   (assets/stock-limit-hint.js); enforceLimits.
 * - An extended warranty can't outnumber its printer: a warranty row's +
 *   stops at the printer's quantity, and lowering the printer lowers the
 *   warranty with it (frontend limit; enforceLimits).
 */
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { enqueue, cartFetch } from '@theme/cart-queue';

const ROOT = window.Shopify?.routes?.root ?? '/';
const QUANTITY_DELAY = 300;
const EXIT_MS = 220;
const ENTER_MS = 260;
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Row ids removed on screen whose removal has not been confirmed yet. */
const pendingRemovals = new Set();
/** Row id -> quantity shown on screen and not confirmed yet. */
const pendingQuantities = new Map();
/** Variant id -> stock cap revealed by an add that came back capped. */
const learnedMax = new Map();
/** Row id -> {variantId, parent, target} waiting for the next batch. */
const toSend = new Map();
let sendTimer = 0;

function sectionIdsOnPage() {
  return [
    ...new Set(
      [...document.querySelectorAll('cart-items-component[data-section-id]')].map(
        (element) => /** @type {HTMLElement} */ (element).dataset.sectionId
      )
    ),
  ].filter(Boolean);
}

/** /cart.js, read once more if the answer is not JSON (seen as an empty body now and then). */
const fetchCart = () => {
  const read = () => fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  return read().catch(read);
};

/** A row's height and vertical spacing: what it collapses from or grows to. */
function boxOf(element) {
  const style = getComputedStyle(element);
  return {
    height: `${element.getBoundingClientRect().height}px`,
    paddingTop: style.paddingTop,
    paddingBottom: style.paddingBottom,
    marginTop: style.marginTop,
    marginBottom: style.marginBottom,
  };
}

const COLLAPSED = { height: '0px', paddingTop: '0px', paddingBottom: '0px', marginTop: '0px', marginBottom: '0px' };

/** Collapse a removed row, then keep it hidden until the cart drops it. */
function leave(row) {
  pendingRemovals.add(row.id);
  row.inert = true;
  row.setAttribute('aria-hidden', 'true');
  if (reducedMotion.matches || typeof row.animate !== 'function') {
    row.hidden = true;
    return;
  }
  row.style.overflow = 'hidden';
  row.style.pointerEvents = 'none';
  const animation = row.animate([{ ...boxOf(row), opacity: 1 }, { opacity: 0, offset: 0.55 }, { ...COLLAPSED, opacity: 0 }], {
    duration: EXIT_MS,
    easing: EASING,
    fill: 'forwards',
  });
  animation.onfinish = () => {
    // Hidden, not removed: the theme's remove buttons address rows by their
    // position until the next re-render.
    row.hidden = true;
  };
}

/** A new line grows into place. */
function arrive(row) {
  if (reducedMotion.matches || typeof row.animate !== 'function') return;
  // Not in a closed drawer: nobody sees it, and measuring the row (boxOf)
  // forces a layout of the whole page - over 200 ms on a collection with a
  // few pages loaded by infinite scroll (CLIENT-155).
  if (row.closest('dialog:not([open])') || document.hidden) return;
  const box = boxOf(row);
  if (box.height === '0px') return;
  row.style.overflow = 'hidden';
  const animation = row.animate([{ ...COLLAPSED, opacity: 0 }, { opacity: 0, offset: 0.4 }, { ...box, opacity: 1 }], {
    duration: ENTER_MS,
    easing: EASING,
  });
  const done = () => {
    row.style.overflow = '';
  };
  animation.onfinish = done;
  animation.oncancel = done;
}

/** Prices that will change: the row's line price, and every cart total on the page. */
function shimmerPrices(row) {
  const targets = [
    ...(row ? row.querySelectorAll('text-component') : []),
    ...document.querySelectorAll('text-component[ref="cartTotal"]'),
  ];
  for (const element of targets) element.setAttribute('shimmer', '');
}

/** The header bubble at once; the cart response confirms it. */
function bumpBubble(delta) {
  if (!delta) return;
  for (const icon of document.querySelectorAll('cart-icon')) {
    const count = Number.parseInt(icon.querySelector('[ref="cartBubbleCount"]')?.textContent ?? '', 10) || 0;
    if (typeof icon.renderCartBubble === 'function') icon.renderCartBubble(Math.max(0, count + delta), false, false);
  }
}

const quantityInputOf = (row) =>
  /** @type {HTMLInputElement | null} */ (row.querySelector('quantity-selector-component input'));

/** Does this /cart.js item belong to the row (same variant, same warranty link)? */
function itemMatches(item, variantId, parent) {
  return String(item.variant_id) === variantId && String(item.properties?.main_variant_id ?? '') === parent;
}

/** Tell the page (bubble, drawer, cart page, card steppers) about the new cart. */
function announce(cart, sections, source) {
  document.dispatchEvent(
    new CartUpdateEvent(cart, source, {
      source,
      itemCount: cart.item_count,
      ...(sections ? { sections } : {}),
    })
  );
}

/**
 * Send collected changes in one go: decreases (and removals) in one
 * /cart/update.js by line key, increases in one /cart/add.js with the same
 * line item properties (merges into the same line). Not increases through
 * update.js: it does not check stock.
 * @param {Map<string, {variantId: string, parent: string, target: number}>} batch
 */
async function sendChanges(batch) {
  const sectionIds = sectionIdsOnPage();
  const withSections = sectionIds.length
    ? { sections: sectionIds.join(','), sections_url: window.location.pathname }
    : {};
  const post = (endpoint, payload) =>
    cartFetch(`${ROOT}cart/${endpoint}.js`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });

  const cart = await fetchCart();
  /** @type {Record<string, number>} */
  const updates = {};
  /** @type {Array<object>} */
  const items = [];
  for (const { variantId, parent, target } of batch.values()) {
    // A product and its warranty go together: removing the product removes
    // the warranty lines tied to it too.
    if (target === 0 && !parent) {
      for (const item of cart.items) {
        if (String(item.properties?.main_variant_id ?? '') === variantId) updates[item.key] = 0;
      }
    }
    const line = cart.items.find((item) => itemMatches(item, variantId, parent));
    if (!line) continue;
    if (target < line.quantity) updates[line.key] = target;
    else if (target > line.quantity) {
      items.push({
        id: line.variant_id,
        quantity: target - line.quantity,
        ...(line.properties && Object.keys(line.properties).length ? { properties: line.properties } : {}),
        ...(line.selling_plan_allocation ? { selling_plan: line.selling_plan_allocation.selling_plan.id } : {}),
      });
    }
  }

  let sections = null;
  if (Object.keys(updates).length) {
    const response = await post('update', { updates, ...(items.length ? {} : withSections) });
    const data = await response.json().catch(() => null);
    if (response.ok && data?.sections) sections = data.sections;
  }
  if (items.length) {
    // 422 over the stock: Shopify still adds what is available.
    const response = await post('add', { items, ...withSections });
    const data = await response.json().catch(() => null);
    sections = response.ok && data?.sections ? data.sections : null;
  }
  const after = await fetchCart();
  // An increase Shopify capped at the stock: remember the cap so + stops there.
  for (const { variantId, parent, target } of batch.values()) {
    const line = after.items.find((item) => itemMatches(item, variantId, parent));
    if (line && line.quantity < target) learnedMax.set(variantId, line.quantity);
  }
  return { cart: after, sections };
}

/**
 * Nothing left on its way: stop the shimmer. A re-render (morph) to the
 * confirmed prices usually ends it already; a response without sections
 * re-renders later, so this makes sure.
 */
function settleShimmer() {
  if (pendingQuantities.size || pendingRemovals.size || toSend.size) return;
  for (const element of document.querySelectorAll('cart-items-component [shimmer], text-component[ref="cartTotal"][shimmer]')) {
    element.removeAttribute('shimmer');
  }
}

/** Queue the rows' changes collected so far. */
function flush(source) {
  clearTimeout(sendTimer);
  if (!toSend.size) return;
  const batch = new Map(toSend);
  toSend.clear();
  enqueue(() => sendChanges(batch))
    .then(({ cart, sections }) => {
      for (const [id, change] of batch) {
        pendingRemovals.delete(id);
        if (pendingQuantities.get(id) === change.target) pendingQuantities.delete(id);
      }
      announce(cart, sections, source);
      settleShimmer();
    })
    .catch((error) => {
      console.error(error);
      for (const id of batch.keys()) {
        pendingRemovals.delete(id);
        pendingQuantities.delete(id);
      }
      fetchCart().then((cart) => announce(cart, null, source));
      settleShimmer();
    });
}

function rowChange(row, target) {
  return { variantId: row.dataset.variantId ?? '', parent: row.dataset.parentProduct ?? '', target };
}

/** Remove a row (and the warranty rows tied to it) at once. */
function removeRow(component, row) {
  if (!row?.id || pendingRemovals.has(row.id)) return;
  observeComponent(component);
  const variantId = row.dataset.variantId ?? '';
  const children = [...component.querySelectorAll('tbody > tr.cart-items__table-row')].filter(
    (other) => other !== row && other.dataset.parentProduct === variantId
  );
  let removed = 0;
  for (const element of [row, ...children]) {
    removed += pendingQuantities.get(element.id) ?? (Number(quantityInputOf(element)?.defaultValue) || 0);
    pendingQuantities.delete(element.id);
    toSend.delete(element.id);
    leave(element);
  }
  bumpBubble(-removed);
  shimmerPrices(null);
  toSend.set(row.id, rowChange(row, 0));
  flush('cart-line-remove');
}

/** The quantity a row shows (pending change first). */
function shownQuantityOf(row) {
  return pendingQuantities.get(row.id) ?? (Number(quantityInputOf(row)?.value) || 0);
}

/** How many of this product (variant) are in the cart rows, not counting rows being removed. */
function productTotal(component, variantId) {
  let total = 0;
  for (const row of component.querySelectorAll('tbody > tr.cart-items__table-row')) {
    if (row.dataset.variantId !== variantId || row.dataset.parentProduct || pendingRemovals.has(row.id)) continue;
    total += shownQuantityOf(row);
  }
  return total;
}

/** How many of this row's variant are in stock (Infinity when not limited). */
function stockMaxOf(row) {
  const input = quantityInputOf(row);
  const fromLiquid = input?.dataset.stockMax === undefined ? NaN : Number(input.dataset.stockMax);
  const caps = [fromLiquid, learnedMax.get(row.dataset.variantId ?? '')].filter(
    (value) => Number.isFinite(value) && value >= 0
  );
  return caps.length ? Math.min(...caps) : Infinity;
}

/** Is this printer (variant id) in the cart, as a row of its own? */
function hasProductRow(component, variantId) {
  return Boolean(
    component.querySelector(`tbody > tr.cart-items__table-row[data-variant-id="${CSS.escape(variantId)}"]`)
  );
}

/**
 * A product's extended warranty rows (data-warranty, snippets/cart-products),
 * in cart order: the newest line first.
 */
function warrantyRowsOf(component, variantId) {
  return [
    ...component.querySelectorAll(
      `tbody > tr.cart-items__table-row[data-warranty][data-parent-product="${CSS.escape(variantId)}"]`
    ),
  ].filter((row) => !pendingRemovals.has(row.id));
}

/**
 * The most this row can go to: its stock, and for an extended warranty what
 * its printer leaves: all the printer's warranties together (1 year, 2 years
 * ...) never more than the printer's quantity.
 */
function rowMax(component, row) {
  let max = stockMaxOf(row);
  const parent = row.dataset.parentProduct;
  if (parent && row.hasAttribute('data-warranty') && hasProductRow(component, parent)) {
    let others = 0;
    for (const other of warrantyRowsOf(component, parent)) if (other !== row) others += shownQuantityOf(other);
    max = Math.min(max, Math.max(0, productTotal(component, parent) - others));
  }
  return max;
}

/**
 * + of every row: marked at its limit (stock, or for a warranty its
 * printer's quantity) with aria-disabled and data-stock-limit, so hover and
 * click say why (assets/stock-limit-hint.js). A warranty above its printer's
 * quantity comes down with it.
 */
function enforceLimits(component) {
  for (const row of component.querySelectorAll('tbody > tr.cart-items__table-row')) {
    if (pendingRemovals.has(row.id)) continue;
    const max = rowMax(component, row);
    const input = quantityInputOf(row);
    if (input && Number.isFinite(max) && input.max !== String(max)) input.max = String(max);
    const plus = /** @type {HTMLElement | null} */ (row.querySelector('.quantity-plus'));
    const quantity = shownQuantityOf(row);
    if (plus) {
      if (quantity >= max) {
        plus.setAttribute('aria-disabled', 'true');
        // The hint names the stock, or for a warranty capped by its printer
        // says it is one per printer.
        const stock = stockMaxOf(row);
        const byStock = Number.isFinite(stock) && stock <= max;
        plus.dataset.stockLimit = byStock ? String(stock) : '';
        if (byStock) delete plus.dataset.limitReason;
        else if (row.hasAttribute('data-warranty')) plus.dataset.limitReason = 'warranty';
      } else {
        plus.removeAttribute('aria-disabled');
        delete plus.dataset.stockLimit;
        delete plus.dataset.limitReason;
      }
    }
  }

  // More warranties than printers (the printer went down, or a cart from
  // before this limit): the newest come down first.
  const printers = new Set(
    [...component.querySelectorAll('tbody > tr.cart-items__table-row[data-warranty]')].map((row) => row.dataset.parentProduct)
  );
  for (const parent of printers) {
    if (!parent || !hasProductRow(component, parent)) continue;
    for (const row of warrantyRowsOf(component, parent)) {
      const rows = warrantyRowsOf(component, parent);
      const excess = rows.reduce((sum, other) => sum + shownQuantityOf(other), 0) - productTotal(component, parent);
      if (excess <= 0) break;
      changeRow(component, row, Math.max(0, shownQuantityOf(row) - excess));
    }
  }
}

/** A quantity typed or stepped in a cart row: shown at once, sent shortly. */
function changeRow(component, row, quantity) {
  if (!row?.id || pendingRemovals.has(row.id)) return;
  // An increase stops at the stock, and a warranty row at its printer. Only
  // increases: a line already above the stock (sold out since it was added)
  // still goes down one by one.
  // (Not the input's value: the theme's stepper has already changed it.)
  const current = pendingQuantities.get(row.id) ?? (Number(quantityInputOf(row)?.defaultValue) || 0);
  if (quantity > current) quantity = Math.min(quantity, Math.max(current, rowMax(component, row)));
  if (quantity <= 0) {
    removeRow(component, row);
    return;
  }
  observeComponent(component);
  const input = quantityInputOf(row);
  const before = pendingQuantities.get(row.id) ?? (Number(input?.defaultValue) || 0);
  pendingQuantities.set(row.id, quantity);
  if (input && input.value !== String(quantity)) input.value = String(quantity);
  bumpBubble(quantity - before);
  shimmerPrices(row);
  toSend.set(row.id, rowChange(row, quantity));
  clearTimeout(sendTimer);
  sendTimer = window.setTimeout(() => flush('cart-line-quantity'), QUANTITY_DELAY);
  enforceLimits(component);
}

// Quantity steppers in cart rows: handled here instead of by the theme's
// cart items component (which locks the list). Capture phase on window, so
// this runs before the theme's listener on document and stops it.
window.addEventListener(
  ThemeEvents.quantitySelectorUpdate,
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const component = target?.closest('cart-items-component');
    const row = target?.closest('tr.cart-items__table-row');
    if (!component || !row) return;
    event.stopImmediatePropagation();
    changeRow(component, row, Number(/** @type {any} */ (event).detail?.quantity) || 0);
  },
  true
);

// The remove button calls CartItemsComponent.onLineItemRemove(line index).
function patch(CartItems) {
  const remove = CartItems.prototype.onLineItemRemove;
  if (typeof remove !== 'function' || remove.__queuedRemove) return;
  /** @this {any} @param {number} line */
  function onLineItemRemove(line) {
    // By cart index, not position: a warranty row renders under its printer,
    // so the DOM order is not the cart order (snippets/cart-products.liquid).
    const row =
      this.querySelector(`tbody > tr.cart-items__table-row[data-index="${line}"]`) ?? this.refs?.cartItemRows?.[line - 1];
    if (!row?.id) return remove.call(this, line);
    removeRow(this, row);
  }
  onLineItemRemove.__queuedRemove = true;
  CartItems.prototype.onLineItemRemove = onLineItemRemove;
}

customElements.whenDefined('cart-items-component').then(() => patch(customElements.get('cart-items-component')));

/** After a re-render: keep what the customer did since that response was sent. */
function reapplyPending(root) {
  for (const id of pendingRemovals) {
    const row = root.querySelector(`[id="${CSS.escape(id)}"]`);
    if (row instanceof HTMLElement && !row.hidden) {
      row.hidden = true;
      row.inert = true;
    }
  }
  for (const [id, quantity] of pendingQuantities) {
    const input = root.querySelector(`[id="${CSS.escape(id)}"] quantity-selector-component input`);
    if (input instanceof HTMLInputElement && input.value !== String(quantity)) input.value = String(quantity);
    const row = root.querySelector(`[id="${CSS.escape(id)}"]`);
    if (row) shimmerPrices(row);
  }
  // Removals still on their way change the total too.
  if (pendingRemovals.size) shimmerPrices(null);
}

const observed = new WeakSet();
const observer = new MutationObserver((mutations) => {
  if (pendingRemovals.size || pendingQuantities.size) {
    const roots = new Set();
    for (const mutation of mutations) {
      const root = mutation.target instanceof Element ? mutation.target.closest('.shopify-section') : null;
      if (root) roots.add(root);
    }
    roots.forEach(reapplyPending);
  }
  // A re-render resets the rows' limits.
  const components = new Set();
  for (const mutation of mutations) {
    const component = mutation.target instanceof Element ? mutation.target.closest('cart-items-component') : null;
    if (component) components.add(component);
  }
  components.forEach(enforceLimits);
  // Only lines that really came in grow into place. A re-render (morph) also
  // takes existing rows out and puts them back when a row above them goes;
  // those keep their id and must not animate again.
  for (const mutation of mutations) {
    if (mutation.target.nodeName !== 'TBODY') continue;
    for (const node of mutation.addedNodes) {
      if (!(node instanceof HTMLTableRowElement) || !node.classList.contains('cart-items__table-row')) continue;
      if (!node.id || knownRows.has(node.id) || node.hidden || pendingRemovals.has(node.id)) continue;
      arrive(node);
    }
  }
  rememberRows();
});

/** Ids of the cart rows on the page after the last change: a row with one
 * of these ids is not new. A line removed and added again animates again. */
const knownRows = new Set();
function rememberRows() {
  knownRows.clear();
  for (const row of document.querySelectorAll('cart-items-component tbody > tr.cart-items__table-row')) {
    if (row.id) knownRows.add(row.id);
  }
}

function observeComponent(component) {
  // The whole section, so a re-render that replaces the component itself is
  // seen too; `hidden` and `value` for rows a re-render patches back.
  const root = component.closest('.shopify-section') ?? component;
  if (observed.has(root)) return;
  observed.add(root);
  observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'value'] });
}

function observeCartItems() {
  rememberRows();
  document.querySelectorAll('cart-items-component').forEach((component) => {
    observeComponent(component);
    enforceLimits(component);
  });
}

observeCartItems();
// A section re-render can bring a new <cart-items-component>.
document.addEventListener(ThemeEvents.cartUpdate, () => setTimeout(observeCartItems));
